<template>
  <div
    class="slider"
    :class="{
      'slider--navigation-outside-container': navigationOutsideContainer,
      'slider--hide-mobile-navigation': hideMobileNavigation,
      'slider--hide-mobile-pagination': hideMobilePagination,
      'slider--pagination': allOptions.pagination,
      'slider--navigation-alt': navigationAlt,
      'slider--loop': allOptions.loop,
      'slider--dark': darkMode,
      'slider--unloaded': !swiper?.slides && slidesPerViewTemp && slidesPerViewTemp !== 'auto',
    }"
  >
    <fn-loading-overlay v-if="loading" />

    <div
      v-show="!loading"
      ref="swiperRef"
      class="swiper-container"
      :class="{
        'swiper-container-gradient-right': !hideGradient && gradientRight,
      }"
    >
      <div
        class="swiper-wrapper"
        :class="[
          {
            'swiper-wrapper--text-captions': textCaptions,
            'swiper-wrapper--pagination': allOptions.pagination,
          },
        ]"
      >
        <slot />
      </div>

      <!-- Pagination  -->
      <template v-if="textCaptions && allOptions.pagination">
        <div class="swiper-pagination-text-captions">
          <div
            v-for="(paginationItem, index) in paginationItems"
            :key="index"
            class="sp-item"
            :class="{
              'sp-item--active': index === swiper?.activeIndex,
            }"
            role="button"
            tabindex="0"
            @click="slideTo(index)"
            @keydown.enter="slideTo(index)"
            @keydown.space.prevent="slideTo(index)"
          >
            <span class="sp-bullet" />
            <span
              v-if="textCaptions"
              class="sp-text"
            >
              {{ paginationItem }}
            </span>
          </div>
        </div>
      </template>
      <div
        v-show="!textCaptions && allOptions.pagination && !swiper?.isLocked && !paginationIsHidden"
        ref="swiperPagination"
        class="swiper-pagination"
      />

      <!-- Navigation  -->
      <template v-if="allOptions.navigation && !navigationOutsideContainer">
        <div
          v-show="!swiper?.isBeginning"
          class="swiper-button-prev"
          :class="{ 'swiper-button-next--alt': navigationAlt }"
          :aria-label="$t('accessibility.slider.btnPrevious')"
          role="button"
          tabindex="0"
          @click="prev()"
          @keydown.enter="prev()"
          @keydown.space.prevent="prev()"
        >
          <fn-icon
            icon="arrow-left"
            :color="navigationAlt ? 'orientation-green' : 'white'"
          />
        </div>
        <div
          v-show="!swiper?.isEnd"
          class="swiper-button-next"
          :class="{ 'swiper-button-next--alt': navigationAlt }"
          :aria-label="$t('accessibility.slider.btnNext')"
          role="button"
          tabindex="0"
          @click="next()"
          @keydown.enter="next()"
          @keydown.space.prevent="next()"
        >
          <fn-icon
            icon="arrow-right"
            :color="navigationAlt ? 'orientation-green' : 'white'"
            size="xxs"
          />
        </div>
      </template>
    </div>
    <template v-if="allOptions.navigation && navigationOutsideContainer">
      <div
        v-show="!swiper?.isBeginning"
        :class="`swiper-button-prev ${navigationOutsideContainer}`"
        :aria-label="$t('accessibility.slider.btnPrevious')"
        role="button"
        tabindex="0"
        @click="prev()"
        @keydown.enter="prev()"
        @keydown.space.prevent="prev()"
      >
        <fn-icon
          icon="arrow-left"
          color="white"
          size="xxs"
        />
      </div>
      <div
        v-show="!swiper?.isEnd"
        :class="`swiper-button-next ${navigationOutsideContainer}`"
        :aria-label="$t('accessibility.slider.btnNext')"
        role="button"
        tabindex="0"
        @click="next()"
        @keydown.enter="next()"
        @keydown.space.prevent="next()"
      >
        <fn-icon
          icon="arrow-right"
          color="white"
          size="xxs"
        />
      </div>
    </template>

    <client-only>
      <template v-if="allOptions.paginationNav && roundedPageSize && swiperSnapGrid.length > 1">
        <div class="slider-pagination-nav">
          <fn-button
            class="spn-button spn-button--prev"
            tertiary
            icon="arrow-left"
            :aria-label="$t('accessibility.slider.btnPrevious')"
            @click="prev()"
            @keydown.enter="prev()"
            @keydown.space.prevent="prev()"
          >
          </fn-button>
          <span class="spn-text"> {{ currentRealIndex }} / {{ roundedPageSize }} </span>
          <fn-button
            class="spn-button spn-button--next"
            tertiary
            icon="arrow-right"
            :aria-label="$t('accessibility.slider.btnNext')"
            @click="next()"
            @keydown.enter="next()"
            @keydown.space.prevent="next()"
          >
          </fn-button>
        </div>
      </template>
    </client-only>
  </div>
</template>

<script setup lang="ts">
import SwiperCore, { Navigation, Pagination, Autoplay, type Swiper, type SwiperOptions } from 'swiper/core';
import type { WatchHandle } from 'vue';

SwiperCore.use([Navigation, Pagination, Autoplay]);

interface Props {
  darkMode?: boolean;
  hideGradient?: boolean;
  gradientRight?: boolean;
  options?: Record<string, any>;
  textCaptions?: boolean;
  navigationOutsideContainer?: string;
  activeSlideIndex?: number;
  hideMobileNavigation?: boolean;
  hideMobilePagination?: boolean;
  singleDotCheck?: boolean;
  loading?: boolean;
  id?: string;
  navigationAlt?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  options: () => ({}),
  navigationOutsideContainer: '',
  activeSlideIndex: 0,
  singleDotCheck: true,
  loading: false,
  id: '',
  hideGradient: true,
});

const emit = defineEmits(['click', 'activeIndexChange']);

const nuxtApp = useNuxtApp();
const slots = useSlots();
const swiperRef = useTemplateRef<HTMLElement>('swiperRef');
const swiperSnapGrid = ref<any[]>([]);
const swiper = ref<Swiper | undefined>();
let initSwiperWatchHandle: WatchHandle;

// region Computed properties
const allOptions = computed(() => {
  return deepMerge(defaultOptions, props.options);
});

const roundedPageSize = computed(() => {
  return unref(swiperSnapGrid).length > 1 ? unref(swiperSnapGrid).length : unref(swiper)?.slides?.length;
});

const currentRealIndex = ref(0);

watchImmediate(
  () => unref(swiper)?.snapIndex,
  (snapIndex) => {
    currentRealIndex.value = Math.max(snapIndex ?? 0, 0) + 1;
  },
);

const paginationItems = computed(() => {
  const titles = [];

  for (const slotRoot of slots.default?.() || []) {
    for (const rootChild of (slotRoot?.children || []) as Array<any>) {
      const title = rootChild?.props?.title || rootChild?.type?.props?.title?.default;

      if (title) {
        titles.push(title);
      }
    }
  }

  return titles?.length ? titles : [];
});

const paginationIsHidden = computed(() => {
  const numberOfDots = unref(swiper)?.pagination?.bullets?.length || 0;
  return props.singleDotCheck && numberOfDots <= 1;
});
// endregion

// region Methods
const initSwiper = async (loading = false) => {
  if (import.meta.server || loading) {
    return;
  }

  await nextTick();

  const _swiperRef = unref(swiperRef);

  if (!_swiperRef) {
    nuxtApp.$log.error("'this.$refs.swiper' is null");
    return;
  }

  // This loses its reactivity in Vue3! In case of problems, add a new state var
  // to data() and update it on a swiper event, see example swiperSnapGrid
  swiper.value = new SwiperCore(_swiperRef, unref(allOptions)) as Swiper;
};

const slideTo = (index: number) => {
  unref(swiper)?.slideTo(index);
};

const handleFadedStatus = () => {
  const _swiperRef = unref(swiperRef);
  const _swiper = unref(swiper);

  if (!_swiper || !_swiperRef) {
    return;
  }

  if (_swiper.isBeginning) {
    _swiperRef.classList.add('swiper-container--reached-beginning');
  } else {
    _swiperRef.classList.remove('swiper-container--reached-beginning');
  }

  if (_swiper.isEnd) {
    _swiperRef.classList.add('swiper-container--reached-end');
  } else {
    _swiperRef.classList.remove('swiper-container--reached-end');
  }

  if (props.hideGradient || props.darkMode) {
    _swiperRef.classList.remove('swiper-container-faded-horizontal');
  }
};

const next = () => {
  unref(swiper)?.slideNext();
};

const prev = () => {
  unref(swiper)?.slidePrev();
};

const forceUpdate = async () => {
  await nextTick();
  swiper?.update?.();

  if (import.meta.client) {
    window.dispatchEvent(new Event('resize'));
  }
};
// endregion

// region Data
const defaultOptions: SwiperOptions = {
  a11y: {
    enabled: true,
  },
  pagination: {
    // With text captions (like in StageSlider) we build our own pagination.
    el: props.textCaptions ? undefined : '.swiper-pagination',
    type: props.textCaptions ? undefined : 'bullets',
    clickable: true,
  },
  navigation: {
    nextEl: props.navigationOutsideContainer
      ? `.swiper-button--next.${props.navigationOutsideContainer}`
      : '.swiper-button--next',
    prevEl: props.navigationOutsideContainer
      ? `.swiper-button--prev.${props.navigationOutsideContainer}`
      : '.swiper-button--prev',
  },
  on: {
    activeIndexChange: (instance) => {
      emit('activeIndexChange', instance.realIndex);
      handleFadedStatus();

      const snapIndex = instance?.snapIndex ?? 0;
      currentRealIndex.value = snapIndex + 1;
    },
    click: (instance) => {
      emit('click', instance.clickedIndex);
    },
    init: () => {
      nextTick(() => {
        handleFadedStatus();
      });
    },
    snapGridLengthChange: (instance) => {
      swiperSnapGrid.value = instance.snapGrid;
    },
  },
};
// endregion

// region Watchers
watch(
  () => props.activeSlideIndex,
  (index) => {
    slideTo(index);
  },
);
// endregion

// region Style fixes for slider not yet loaded
const getBreakpoint = (breakpoints: Record<string, Record<string, any>>, index: number) => {
  if (!breakpoints) {
    return undefined;
  }

  for (const key in breakpoints) {
    if (index <= parseInt(key)) {
      return breakpoints[key];
    }
  }

  const validKeys = Object.keys(breakpoints || {})
    .map(Number)
    .sort((keyA, keyB) => keyA - keyB);

  const key = Math.max(...validKeys);
  return { ...breakpoints[key], key };
};

const slidesPerViewTemp = ref();
const spaceBetweenTemp = ref();

if (import.meta.client) {
  const breakpointOption = getBreakpoint(props.options?.breakpoints, window.document.documentElement.clientWidth);
  slidesPerViewTemp.value = breakpointOption?.slidesPerView ?? props.options?.slidesPerView ?? 1;
  spaceBetweenTemp.value = breakpointOption?.spaceBetween ?? props.options?.spaceBetween ?? 16;
} else {
  slidesPerViewTemp.value = props.options?.slidesPerView ?? 1;
  spaceBetweenTemp.value = props.options?.spaceBetween ?? 16;
}

const slidesPerViewLow = ref();
const spaceBetweenLow = ref();
const breakpointLow = ref();

const slidesPerViewHigh = ref();
const spaceBetweenHigh = ref();
const breakpointHigh = ref();

const bpLow = getBreakpoint(props.options?.breakpoints, 0);
const bpHigh = getBreakpoint(props.options?.breakpoints, 9999);

slidesPerViewLow.value = bpLow?.slidesPerView ?? 1;
spaceBetweenLow.value = bpLow?.spaceBetween ?? 16;
breakpointLow.value = bpLow?.key;

slidesPerViewHigh.value = bpHigh?.slidesPerView ?? 1;
spaceBetweenHigh.value = bpHigh?.spaceBetween ?? 16;
breakpointHigh.value = bpHigh?.key;

if (slidesPerViewLow.value === 'auto') {
  slidesPerViewLow.value = undefined;
}
if (slidesPerViewHigh.value === 'auto') {
  slidesPerViewHigh.value = undefined;
}
// endregion

// region Lifecycle Hooks
onMounted(async () => {
  await initSwiper();
  initSwiperWatchHandle = watch(() => props.loading, initSwiper);
});

onUnmounted(() => {
  initSwiperWatchHandle?.stop();
});

// Make sure that our swiper slides have the correct width, at any time.
// Watch for rendering (also re-rendering) to trigger forceUpdate
// https://vuejs.org/guide/essentials/watchers#post-watchers
watchPostEffect(() => {
  forceUpdate();
});

onMounted(forceUpdate);
// endregion
</script>

<style lang="scss" scoped>
@import 'swiper/swiper.scss';

$themeColor: $color-brand-fnmz-green;
$colors: (
  white: $color-white,
  black: palette(black),
);

@import 'swiper/components/pagination/pagination.scss';
@import 'swiper/components/navigation/navigation.scss';

// overwrites & custom css
$swiper-button-size: 40px;
$swiper-button-size--alt: 40px;

.slider {
  position: relative;

  &.slider--navigation-outside-container {
    padding: 0 $swiper-button-size * 1.5;

    &.slider--hide-mobile-navigation {
      @include bp(m, max) {
        padding: 0;
      }
    }

    .swiper-button-next {
      right: 0;
    }

    .swiper-button-prev {
      left: 0;
    }
  }

  .slider-pagination-nav {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: $pad 0;

    .spn-button {
      cursor: pointer;

      &.spn-button--prev {
        margin-right: $pad * 3.5;
        :deep(.icon) {
          right: 2px;
        }
      }

      &.spn-button--next {
        margin-left: $pad * 3.5;
        :deep(.icon) {
          left: 2px;
        }
      }
    }
  }

  // Fixes zero-width slides by providing missing styles until the slider has been fully loaded.
  // We don't have viewport dimensions during SSR, so we always use the smallest mobile breakpoint when available.
  // While it works well for mobile devices, it's suboptimal for desktops. Yet, better than having no slider at all.
  // Note: Hope this becomes obsolete when we update the library at some point
  &.slider--unloaded {
    @include bp(s, max) {
      // Slides per view, plain number
      --slide-per-view: v-bind(slidesPerViewLow);

      // Spacing between slides, converted to pixel
      --space-between: v-bind(spaceBetweenLow) * 1px;

      // Offset is approximated via trial&error
      --slide-reduction: (var(--space-between) * (var(--slide-per-view) - 1) / (var(--slide-per-view)));

      .swiper-wrapper {
        width: 100%;

        :deep(.swiper-slide) {
          width: calc(100% / var(--slide-per-view) - var(--slide-reduction) - ((var(--slide-per-view) - 1) * 2px));
          margin-right: calc(var(--space-between) + (var(--slide-per-view) * 2px));
        }
      }
    }

    @include bp(s) {
      // Slides per view, plain number
      --slide-per-view: v-bind(slidesPerViewHigh);

      // Spacing between slides, converted to pixel
      --space-between: v-bind(spaceBetweenHigh) * 1px;

      // Offset is approximated via trial&error
      --slide-reduction: var(--space-between) * (var(--slide-per-view) - 1) / (var(--slide-per-view));

      .swiper-wrapper {
        width: 100%;

        :deep(.swiper-slide) {
          width: calc(100% / var(--slide-per-view) - var(--slide-reduction) - ((var(--slide-per-view) - 1) * 2px));
          margin-right: calc(var(--space-between) + (var(--slide-per-view) * 2px));
        }
      }
    }
  }

  &.slider--loop {
    .swiper-button-prev {
      opacity: 1;
    }
    .swiper-button-next {
      opacity: 1;
    }
  }

  &.slider--hide-mobile-navigation {
    & .swiper-button-next,
    & .swiper-button-prev {
      display: none;
    }

    @include bp(m) {
      & .swiper-button-next,
      & .swiper-button-prev {
        display: flex;
      }
    }
  }

  &.slider--hide-mobile-pagination {
    @include bp(s, max) {
      & .slider-pagination-nav {
        display: none;
      }
    }
  }

  &.slider--dark .swiper-container {
    :deep(.pricing) {
      &,
      .p-price,
      .p-per-unit,
      .p-former {
        color: $color-general-text-inverse-default;
      }
    }

    :deep(.ars-pt) {
      background-color: transparent;
    }

    :deep(.product-teaser) {
      background-color: transparent;
      color: $color-general-text-inverse-default;

      .pt-figure {
        background-color: $color-general-background-neutral-default;
      }
    }
  }
}

:deep(.swiper-pagination-bullets) {
  display: flex;
  justify-content: center;
  column-gap: 5px;

  .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: palette(grey, light);
    transition: 0.2s all;
    cursor: pointer;
    &.swiper-pagination-bullet-active {
      background-color: $color-brand-fnmz-green;
    }
  }
  // The lines below are needed to hide bullets on dev and stage env
  // Please do not remove them, even if there is no changes on your local

  .swiper-pagination-bullets.swiper-pagination-lock {
    display: none;
  }
}

.swiper-wrapper {
  &.swiper-wrapper--pagination {
    padding-bottom: 40px;
  }

  &.swiper-wrapper--text-captions {
    padding-bottom: 0;
  }
}

%_swiper-button {
  width: $swiper-button-size;
  height: $swiper-button-size;
  background: none;
  z-index: 4;

  .slider--pagination &,
  .swiper-wrapper--pagination & {
    top: calc(50% - $swiper-button-size); // correction for bullets outside
  }

  &:before {
    content: '';
    width: $swiper-button-size;
    height: $swiper-button-size;
    display: block;
    background: $color-general-background-neutral-default;
    border: 2px solid $color-action-border-primary-default-base;
    border-radius: 100%;
  }

  &:focus {
    outline: none;
  }
  &:focus-visible {
    @include focus-outline-default;
  }

  .icon {
    @include center;
    color: $color-action-border-primary-default-base;
    font-size: map-get($font-sizes, 'xl');
  }
}

.swiper-button-next {
  @extend %_swiper-button;
  right: -12px;

  .icon {
    width: 17px;
  }

  &:after {
    content: '';
  }
}

.swiper-button-prev {
  @extend %_swiper-button;
  left: -12px;

  .icon {
    width: 22px;
  }

  &:after {
    content: '';
  }
}

.slider--navigation-alt {
  .swiper-button-next,
  .swiper-button-prev {
    top: calc(50% - 40px);
    transform: translateY(-50%);
    width: $swiper-button-size--alt;
    height: $swiper-button-size--alt;

    &:before {
      background: $color-general-background-neutral-default;
      border: 1px solid palette(green, light);
      width: 100%;
      height: 100%;
    }
  }
  .swiper-button-next {
    right: 0;
  }

  .swiper-button-prev {
    left: 0;
  }
}

.swiper-pagination-text-captions {
  @include constrained($content-max-width);
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @include bp(m) {
    margin-top: 0;
    border-top: 3px solid palette(sand);
  }

  .sp-item {
    cursor: pointer;
    justify-content: center;
    display: flex;

    @include bp(m) {
      flex: 1 1 100%;
    }

    &.sp-item--active {
      .sp-bullet:after {
        background-color: $color-brand-fnmz-green;
      }

      .sp-text {
        border-color: green;
      }
    }

    .sp-bullet {
      padding: 10px 3px;

      @include bp(m) {
        display: none;
      }

      &:after {
        display: block;
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 100%;
        background-color: palette(grey, light);
      }
    }

    .sp-text {
      display: none;
      position: relative;
      top: -3px;
      border-top: 3px solid transparent;
      padding: 10px;
      text-align: center;

      @include bp(m) {
        display: inline-block;
      }
    }
  }
}

.swiper-container-gradient-right {
  margin-right: -10px;

  @include bp(m) {
    margin-right: 0;
  }

  &:after {
    content: '';
    height: 100%;
    position: absolute;
    display: none;
    width: 10px;
    top: 0;
    bottom: 0;
    z-index: 3;
    pointer-events: none;
    transition: 0.2s ease-in all;
    opacity: 1;

    @include bp(m) {
      display: block;
      width: 100px;
    }
  }

  &:after {
    background: linear-gradient(-90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    right: 0;
  }
}

.swiper-container-faded-horizontal {
  margin-right: -10px;

  &:before,
  &:after {
    content: ' ';
    height: 100%;
    position: absolute;
    display: none;
    width: 10px;
    top: 0;
    bottom: 0;
    z-index: 3;
    pointer-events: none;
    transition: 0.2s ease-in all;
    opacity: 1;

    @include bp(m) {
      display: block;
      width: 100px;
    }
  }

  &:before {
    background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    left: 0;
  }

  &:after {
    background: linear-gradient(-90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    right: 0;
  }

  @each $palette, $tones in $palettes {
    @each $tone, $hex in $tones {
      @if $tone == 'base' {
        .section--bg-#{$palette} &,
        .panel--bg-#{$palette} & {
          &:before {
            background: linear-gradient(90deg, rgba($hex, 1) 0%, rgba($hex, 0) 100%);
          }

          &:after {
            background: linear-gradient(-90deg, rgba($hex, 1) 0%, rgba($hex, 0) 100%);
          }
        }
      } @else {
        .section--bg-#{$palette}-#{$tone} &,
        .panel--bg-#{$palette}-#{$tone} & {
          &:before {
            background: linear-gradient(90deg, rgba($hex, 1) 0%, rgba($hex, 0) 100%);
          }

          &:after {
            background: linear-gradient(-90deg, rgba($hex, 1) 0%, rgba($hex, 0) 100%);
          }
        }
      }
    }
  }

  @include bp(m) {
    margin-right: 0;
  }
}

.swiper-container--reached-beginning {
  .swiper-button-prev {
    opacity: 0;
  }

  &:before {
    opacity: 0;
  }
}

.swiper-container--reached-end {
  .swiper-button-next {
    opacity: 0;
  }

  &:after {
    opacity: 0;
  }
}
</style>
